import React, { useMemo, useRef, useState, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { lazyPage, Spinner, useMediaQuery } from "@digitalisation/enlixy-comp";
import Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import { useThemedCharts } from "../../hooks/useThemedCharts";
import { useChartCommentPopover } from "./ChartCommentPanel";
import { useChartComments } from "../../hooks/useChartComments";
import { useSettingsStore } from "../../store/SettingsStore";
import { track } from "../../help/learning";
import { attachPointContextMenu } from "./pointContextMenu";
import type { ChartComment } from "../../services/ChartCommentService";

// Reloads the page once when the part is gone after a deploy.
const ChartCommentPopover = lazyPage(() => import("./ChartCommentPanel"));

// Import exporting modules for Highstock
import "highcharts/modules/exporting";
import "highcharts/modules/offline-exporting";
import "highcharts/modules/export-data";

// Deep merge function for options
const deepMerge = (target: any, source: any): any => {
  const result = { ...target };
  for (const key in source) {
    if (
      source[key] &&
      typeof source[key] === "object" &&
      !Array.isArray(source[key])
    ) {
      result[key] = deepMerge(target[key] || {}, source[key]);
    } else {
      result[key] = source[key];
    }
  }
  return result;
};

/** A series point: either a `[timestamp, value]` tuple or a point object. */
type StockPoint =
  | [number, number | null]
  | { x: number; y: number | null; custom?: { actual?: number } };

/** Timestamp of a point, whichever form it takes. */
const pointX = (p: StockPoint): number => (Array.isArray(p) ? p[0] : p.x);

/**
 * Value of a point for export/tabular display: the unclamped `custom.actual`
 * when present, so a capped bar still reports its real figure.
 */
const pointY = (p: StockPoint): number | null =>
  Array.isArray(p) ? p[1] : (p.custom?.actual ?? p.y);

export interface BaseStockChartProps {
  /** Chart type */
  type?: "line" | "column" | "bar" | "area" | "spline" | "areaspline";
  /** Chart title */
  title?: string;
  /** Chart subtitle */
  subtitle?: string;
  /** Chart height in pixels */
  height?: number;
  /** Chart series data with timestamps */
  series: Array<{
    name: string;
    /**
     * `[timestamp, value]` pairs, or point objects when a series needs to carry
     * extra data. `custom.actual` holds the unclamped value for points whose
     * `y` was capped to keep the axis scale readable — the tooltip shows it in
     * place of `y` (see the default formatter).
     */
    data: StockPoint[];
    color?: string;
    type?: "line" | "column" | "bar" | "area" | "spline" | "areaspline";
    /**
     * Index into a multi-axis `customOptions.yAxis` array. Only needed when a
     * chart plots two different units together (e.g. units/min against a
     * percentage); omit for the normal single-axis case.
     */
    yAxis?: number;
    dashStyle?: "Solid" | "Dash" | "Dot" | "ShortDot";
    /** Draw a line series as steps (e.g. a target that changes per bucket). */
    step?: "left" | "center" | "right";
  }>;
  /** Y-axis configuration */
  yAxis?: {
    title?: string;
    min?: number;
    max?: number;
    /**
     * Baseline scale: the axis spans at least this range but expands for
     * outliers. Prefer these over a hard `min`/`max` for percentage charts —
     * a hard `max` clips points above it out of the plot area, which also makes
     * them un-hoverable (no tooltip for values over 100%).
     */
    softMin?: number;
    softMax?: number;
    /** Let the axis end between ticks, so a hard min/max is honoured exactly. */
    startOnTick?: boolean;
    endOnTick?: boolean;
    format?: string;
    plotLines?: Array<{
      value: number;
      color?: string;
      dashStyle?: "Solid" | "Dash" | "Dot";
      width?: number;
      label?: string;
    }>;
  };
  /** Loading state */
  loading?: boolean;
  /** Error message */
  error?: string | null;
  /** Custom CSS class */
  className?: string;
  /** Legend configuration */
  legend?: {
    enabled?: boolean;
    position?: "top" | "bottom" | "left" | "right";
  };
  /** Tooltip customization */
  tooltip?: {
    suffix?: string;
    prefix?: string;
    formatter?: () => string;
  };
  /** Custom chart options to override defaults */
  customOptions?: Partial<Highcharts.Options>;
  /** Pan and zoom configuration */
  panZoom?: {
    enabled?: boolean;
    type?: "x" | "y" | "xy";
    mouseWheel?: boolean;
    pinchZoom?: boolean;
  };
  /** Navigator configuration */
  navigator?: {
    enabled?: boolean;
    height?: number;
    maskFill?: string;
    outlineColor?: string;
    outlineWidth?: number;
    handles?: {
      backgroundColor?: string;
      borderColor?: string;
    };
  };
  /** Range selector configuration */
  rangeSelector?: {
    enabled?: boolean;
    buttons?: Array<{
      type?:
        | "millisecond"
        | "second"
        | "minute"
        | "hour"
        | "day"
        | "week"
        | "month"
        | "year"
        | "all";
      count?: number;
      text: string;
    }>;
  };
  /** Callback for retry action */
  onRetry?: () => void;
  /** No data message */
  noDataMessage?: string;
  /** Callback for range selection change */
  onRangeChange?: (min: number, max: number) => void;
  /** Optional sync group for Highcharts synchronization */
  syncGroup?: string;
  /** Force reset zoom when this value changes */
  forceResetZoom?: boolean | number;
  /** Export functionality configuration */
  exporting?: {
    enabled?: boolean;
    filename?: string;
  };
  /** Extra controls to render on the left side of the button row */
  extraControls?: React.ReactNode;
  /** Chart comment props — if chartType is set, the comment button appears */
  commentProps?: {
    chartType: string;
    machineBK?: string;
    shifts?: string[];
    timestampMode?: boolean;
  };
  /**
   * LEFT-click on a point (comments are on right-click). Used for drill-down:
   * clicking a shift bar opens that shift's detail chart.
   *
   * The callback is read through a ref, so its identity is free to change
   * between renders without triggering a chart redraw.
   */
  onPointClick?: (point: StockPointClick) => void;
}

/** What a left-click reports back to the caller. */
export interface StockPointClick {
  /** Point timestamp (ms). */
  x: number;
  /** Plotted value — capped for clamped series; see `custom.actual`. */
  y: number | null;
  /** Uncapped value when the series carries one, else `y`. */
  actual: number | null;
  seriesName: string;
}

const BaseStockChart: React.FC<BaseStockChartProps> = ({
  type = "column",
  title,
  subtitle,
  height = 400,
  series = [],
  yAxis = {},
  loading = false,
  error = null,
  className = "",
  legend = { enabled: true, position: "bottom" },
  tooltip = {},
  customOptions = {},
  panZoom = { enabled: true, type: "x", mouseWheel: true, pinchZoom: true },
  navigator = {
    enabled: true,
    height: 80,
    maskFill: "rgba(37, 99, 235, 0.15)",
    outlineColor: "#2563eb",
    outlineWidth: 2,
    handles: {
      backgroundColor: "#2563eb",
      borderColor: "#1d4ed8",
    },
  },
  rangeSelector = { enabled: false },
  onRetry,
  noDataMessage = "No data available",
  onRangeChange,
  syncGroup,
  forceResetZoom,
  exporting = { enabled: true, filename: "stock-chart" },
  extraControls,
  commentProps,
  onPointClick,
}) => {
  const { popoverPoint, openPopover, closePopover } = useChartCommentPopover();
  const { comments: chartComments, refetch: refetchComments } =
    useChartComments({
      chartType: commentProps?.chartType ?? "",
      machineBK: commentProps?.machineBK,
    });
  const demoMode = useSettingsStore((s) => s.demoMode);
  /** See BaseChart: demo mode hides the affordance, not just the fetch. */
  const commentsEnabled = !demoMode && !!commentProps?.chartType;
  const containerRef = useRef<HTMLDivElement>(null);
  const commentMarkersRef = useRef<any[]>([]);
  /**
   * The marker hover callout, if one is open. It's rendered at the chart root
   * rather than inside the marker group, so destroying the markers does NOT
   * remove it: a redraw while hovering killed the group before its `mouseout`
   * could fire and left the callout stuck on screen.
   */
  const commentTooltipRef = useRef<any>(null);
  /**
   * Signature of the last marker draw (comments + visible window + plot
   * geometry). `drawMarkers` runs on every chart render, so without this the
   * markers were destroyed and rebuilt constantly — wasted work, and visible
   * flicker on live-updating charts.
   */
  const lastMarkerKeyRef = useRef<string>("");

  const destroyCommentTooltip = React.useCallback(() => {
    if (!commentTooltipRef.current) return;
    try {
      commentTooltipRef.current.destroy();
    } catch {
      /* already destroyed with the chart */
    }
    commentTooltipRef.current = null;
  }, []);

  /**
   * Latest left-click handler, held in a ref so `chartOptions` doesn't depend on
   * its identity: callers commonly pass an inline closure, and any new identity
   * in the options would rebuild them and redraw the whole chart.
   */
  const onPointClickRef = useRef(onPointClick);
  onPointClickRef.current = onPointClick;
  const isClickable = !!onPointClick;

  const { t } = useTranslation();
  // Same breakpoint as before (window.innerWidth < 768).
  const isMobile = useMediaQuery("(max-width: 767.98px)");
  const [showData, setShowData] = useState(false);

  // Export functions for raw data
  const exportToCSV = () => {
    track("chart_exported");
    const csvContent = generateCSVContent();
    downloadFile(
      csvContent,
      `${exporting.filename || "stock-chart"}.csv`,
      "text/csv",
    );
  };

  const exportToExcel = () => {
    track("chart_exported");
    const csvContent = generateCSVContent();
    downloadFile(
      csvContent,
      `${exporting.filename || "stock-chart"}.xls`,
      "application/vnd.ms-excel",
    );
  };

  const generateCSVContent = () => {
    // Collect all unique timestamps
    const allTimestamps = Array.from(
      new Set(series.flatMap((s) => s.data.map(pointX))),
    ).sort((a, b) => a - b);

    // Create CSV header
    const headers = ["Timestamp", ...series.map((s) => s.name)];
    let csvContent = headers.join(",") + "\n";

    // Add data rows
    allTimestamps.forEach((ts) => {
      const row = [new Date(ts).toISOString()];
      series.forEach((s) => {
        const found = s.data.find((d) => pointX(d) === ts);
        const value = found ? pointY(found) : null;
        row.push(value !== null && value !== undefined ? value.toString() : "");
      });
      csvContent += row.map((cell) => `"${cell}"`).join(",") + "\n";
    });

    return csvContent;
  };

  const downloadFile = (
    content: string,
    filename: string,
    mimeType: string,
  ) => {
    const blob = new Blob([content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Get themed chart configurations
  const { getChartConfig, getSeriesColor, getStatusColors, isDark } =
    useThemedCharts();
  const statusColors = getStatusColors;

  // Theme-aware navigator defaults.
  // Memoised because `chartOptions` depends on it: as a bare object literal it
  // got a new identity on every render, so any local state change (opening the
  // comment popover, toggling the data table) rebuilt the options and made
  // Highcharts redraw the whole chart.
  const themedNavigator = useMemo(
    () => ({
      enabled: true,
      height: 80,
      maskFill: isDark ? "rgba(59, 130, 246, 0.15)" : "rgba(37, 99, 235, 0.15)",
      outlineColor: statusColors.info,
      outlineWidth: 2,
      handles: {
        backgroundColor: statusColors.info,
        borderColor: statusColors.info,
      },
      ...navigator,
    }),
    [isDark, statusColors.info, navigator],
  );

  const chartRef = useRef<any>(null);
  const [isZoomed, setIsZoomed] = useState(false);
  const isResettingRef = useRef(false);

  // Highcharts sync functionality - synchronize tooltips, crosshairs, and zoom across charts with the same syncGroup
  React.useEffect(() => {
    if (!syncGroup || !chartRef.current?.chart) return;

    const chart = chartRef.current.chart;

    // Sync mouse events (tooltip and crosshair)
    const syncMouseEvents = (e: any) => {
      if (!chart.pointer || !chart.series[0]) return;
      const event = chart.pointer.normalize(e.detail);
      const point = chart.series[0].searchPoint(event, true);

      if (point) {
        point.onMouseOver();
        chart.tooltip.refresh(point);
        chart.xAxis[0].drawCrosshair(event, point);
      }
    };

    // Sync zoom/extremes changes
    const syncExtremes = (e: any) => {
      const { min, max, trigger } = e.detail;
      if (chart.xAxis[0] && trigger === "sync") {
        // Set extremes without triggering events to avoid loops
        chart.xAxis[0].setExtremes(min, max, true, false, { trigger: "sync" });
      } else if (chart.xAxis[0] && trigger === "reset") {
        // Handle reset zoom sync
        chart.xAxis[0].setExtremes(null, null, true, false, {
          trigger: "sync",
        });
        setIsZoomed(false);
      }
    };

    // Event names for this sync group
    const mouseMoveEvent = `mousemove.sync.${syncGroup}`;
    const extremesEvent = `extremes.sync.${syncGroup}`;

    // Listen for sync events from other charts
    window.addEventListener(mouseMoveEvent, syncMouseEvents);
    window.addEventListener(extremesEvent, syncExtremes);

    // Emit mouse events from this chart
    const container = chart.container;
    const emitMouseMove = (e: any) => {
      const customEvent = new CustomEvent(mouseMoveEvent, { detail: e });
      window.dispatchEvent(customEvent);
    };

    container.addEventListener("mousemove", emitMouseMove);

    // Cleanup
    return () => {
      window.removeEventListener(mouseMoveEvent, syncMouseEvents);
      window.removeEventListener(extremesEvent, syncExtremes);
      container.removeEventListener("mousemove", emitMouseMove);
    };
  }, [syncGroup, chartRef.current?.chart]);

  // Simple reset function that we can call
  const resetZoom = () => {
    if (chartRef.current && chartRef.current.chart) {
      const chart = chartRef.current.chart;
      if (chart.xAxis && chart.xAxis[0]) {
        // Set flag to prevent event handler from interfering
        isResettingRef.current = true;
        setIsZoomed(false);

        chart.xAxis[0].setExtremes(null, null);

        // Reset flag after a delay
        setTimeout(() => {
          isResettingRef.current = false;
        }, 100);

        // Emit sync event to reset all charts in the group
        if (syncGroup) {
          const extremesEvent = new CustomEvent(`extremes.sync.${syncGroup}`, {
            detail: { min: null, max: null, trigger: "reset" },
          });
          window.dispatchEvent(extremesEvent);
        }

        if (onRangeChange) {
          // Get the full data range
          const series = chart.series[0];
          if (series && series.data.length > 0) {
            const firstPoint = series.data[0];
            const lastPoint = series.data[series.data.length - 1];
            if (
              firstPoint &&
              lastPoint &&
              firstPoint.x !== undefined &&
              lastPoint.x !== undefined
            ) {
              onRangeChange(firstPoint.x, lastPoint.x);
            }
          }
        }
      }
    }
  };

  // Effect to handle forced zoom reset
  React.useEffect(() => {
    if (forceResetZoom !== undefined) {
      resetZoom();
    }
  }, [forceResetZoom]);

  // Effect to handle chart reflow when theme changes
  React.useEffect(() => {
    if (chartRef.current?.chart) {
      // Multiple attempts to ensure proper resizing with plot area preservation
      const timers = [
        setTimeout(() => {
          if (chartRef.current?.chart) {
            const chart = chartRef.current.chart;
            chart.setSize(undefined, height, { duration: 0 });
            chart.reflow();
            // Force redraw to ensure proper rendering
            chart.redraw();
          }
        }, 50),
        setTimeout(() => {
          if (chartRef.current?.chart) {
            const chart = chartRef.current.chart;
            // Ensure the chart container has the correct height
            if (chart.container && chart.container.parentElement) {
              chart.container.parentElement.style.height = `${height}px`;
            }
            chart.setSize(undefined, height, { duration: 0 });
            chart.reflow();
            chart.redraw();
          }
        }, 200),
        setTimeout(() => {
          if (chartRef.current?.chart) {
            const chart = chartRef.current.chart;
            chart.reflow();
            // Final check to ensure proper height
            if (chart.chartHeight !== height) {
              chart.setSize(undefined, height, { duration: 0 });
            }
          }
        }, 500),
      ];

      return () => timers.forEach((timer) => clearTimeout(timer));
    }
  }, [isDark, height]); // Watch both theme and height changes

  // ResizeObserver effect to handle container size changes
  React.useEffect(() => {
    const chartContainer = chartRef.current?.container?.parentElement;
    if (!chartContainer || !window.ResizeObserver) return;

    const resizeObserver = new ResizeObserver(() => {
      if (chartRef.current?.chart) {
        // Debounce the resize
        setTimeout(() => {
          if (chartRef.current?.chart) {
            chartRef.current.chart.reflow();
          }
        }, 100);
      }
    });

    resizeObserver.observe(chartContainer);

    return () => {
      resizeObserver.disconnect();
    };
  }, [chartRef.current?.chart]);

  // Track when chart is loaded
  const [chartLoaded, setChartLoaded] = React.useState(false);

  // ─── Point RIGHT-click → open comment popover ───
  // Highcharts has no point-level contextmenu event, so the listener sits on the
  // container and resolves the point under the cursor. Left-click is left alone
  // for zoom/selection.
  React.useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart || !commentsEnabled) return;

    return attachPointContextMenu(chart, (point) => {
      const c = point.series.chart;
      const p = point as unknown as { plotX?: number; plotY?: number };
      const plotX = (p.plotX ?? 0) + c.plotLeft;
      const plotY = (p.plotY ?? 0) + c.plotTop;
      const ts = point.x as number;
      const seriesName = point.series.name;
      const iso = new Date(ts).toISOString();

      // Shift-based series (e.g. "Shift A1", "Efficiency A1") anchor the comment
      // to a shift; everything else anchors to the timestamp.
      const shiftMatch = seriesName.match(/\s([A-Z]\d*)$/i);

      openPopover({
        x: plotX,
        y: plotY,
        category: shiftMatch ? iso.split("T")[0] : iso,
        seriesName,
        ...(shiftMatch
          ? { shiftDate: iso.split("T")[0], shiftName: shiftMatch[1] }
          : { timestamp: iso }),
      });
    });
  }, [commentsEnabled, openPopover, chartLoaded]);

  // ─── Comment markers: draw 💬 icons at timestamps with comments ───
  React.useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart || !commentsEnabled) return;

    const drawMarkers = () => {
      const xAxis = chart.xAxis?.[0];
      const ext = xAxis?.getExtremes();

      // Bail out when nothing affecting the markers changed. `render` fires on
      // every redraw, so this is what stops the constant destroy-and-rebuild
      // cycle (and the flicker it caused on live charts).
      const key = [
        chartComments.map((c) => c.id).join(","),
        ext?.min,
        ext?.max,
        chart.plotLeft,
        chart.plotTop,
        chart.plotWidth,
        chart.plotHeight,
        isDark,
      ].join("|");
      if (key === lastMarkerKeyRef.current) return;
      lastMarkerKeyRef.current = key;

      // Clean up previous markers, and the hover callout with them: the group it
      // belongs to is about to be destroyed, so its `mouseout` would never fire
      // and the callout would be orphaned on the chart.
      destroyCommentTooltip();
      commentMarkersRef.current.forEach((m) => {
        try {
          m.destroy();
        } catch {}
      });
      commentMarkersRef.current = [];

      if (!chartComments.length) return;
      if (!xAxis || !ext) return;
      const extremes = ext;

      // Helper to get timestamp from comment (handles both shift_date and timestamp)
      const getTs = (c: ChartComment): number => {
        if (c.timestamp) return new Date(c.timestamp).getTime();
        if (c.shift_date) return new Date(c.shift_date).getTime();
        return 0;
      };

      // Group comments by date+shift (for shift-based) or timestamp (for time-based)
      // Key format: "timestamp|shiftName" for shift-based, "timestamp|" for time-based
      const grouped = new Map<string, ChartComment[]>();
      for (const c of chartComments) {
        const ts = getTs(c);
        if (!ts) continue;
        // Skip if outside visible range
        if (ts < extremes.min || ts > extremes.max) continue;
        // For shift-based, group by day + shift; for timestamp-based, group by minute
        const tsKey = c.shift_date
          ? new Date(c.shift_date).setHours(0, 0, 0, 0)
          : Math.round(ts / 60000) * 60000;
        const shiftKey = c.shift_name || "";
        const key = `${tsKey}|${shiftKey}`;
        const arr = grouped.get(key) || [];
        arr.push(c);
        grouped.set(key, arr);
      }

      // Collect all unique shifts to calculate offset
      const shiftsAtDate = new Map<number, string[]>();
      grouped.forEach((_, compositeKey) => {
        const [tsKeyStr, shiftName] = compositeKey.split("|");
        const tsKey = parseInt(tsKeyStr, 10);
        if (!shiftsAtDate.has(tsKey)) shiftsAtDate.set(tsKey, []);
        if (shiftName && !shiftsAtDate.get(tsKey)!.includes(shiftName)) {
          shiftsAtDate.get(tsKey)!.push(shiftName);
        }
      });

      grouped.forEach((cmts, compositeKey) => {
        const [tsKeyStr, shiftName] = compositeKey.split("|");
        const tsKey = parseInt(tsKeyStr, 10);
        let pixelX = xAxis.toPixels(tsKey, false);

        // Offset markers horizontally if multiple shifts on same day
        const shifts = shiftsAtDate.get(tsKey) || [];
        if (shiftName && shifts.length > 1) {
          const shiftIdx = shifts.sort().indexOf(shiftName);
          const totalShifts = shifts.length;
          const spacing = 28; // pixels between shift markers
          const totalWidth = (totalShifts - 1) * spacing;
          pixelX = pixelX - totalWidth / 2 + shiftIdx * spacing;
        }

        const count = cmts.length;

        const group = chart.renderer
          .g("comment-marker")
          .attr({ zIndex: 10 })
          .add();

        // Light grey dashed vertical line spanning full chart height
        chart.renderer
          .path([
            "M",
            pixelX,
            chart.plotTop,
            "L",
            pixelX,
            chart.plotTop + chart.plotHeight,
          ] as any)
          .attr({
            stroke: isDark ? "#64748b" : "#cbd5e1",
            "stroke-width": 1,
            "stroke-dasharray": "4,4",
            zIndex: 5,
          })
          .add(group);

        // Chat bubble icon - neutral/grey style
        const iconY = chart.plotTop + 4;
        chart.renderer
          .path([
            "M",
            pixelX - 8,
            iconY + 2,
            "L",
            pixelX + 8,
            iconY + 2,
            "Q",
            pixelX + 11,
            iconY + 2,
            pixelX + 11,
            iconY + 5,
            "L",
            pixelX + 11,
            iconY + 14,
            "Q",
            pixelX + 11,
            iconY + 17,
            pixelX + 8,
            iconY + 17,
            "L",
            pixelX - 3,
            iconY + 17,
            "L",
            pixelX - 6,
            iconY + 21,
            "L",
            pixelX - 5,
            iconY + 17,
            "L",
            pixelX - 8,
            iconY + 17,
            "Q",
            pixelX - 11,
            iconY + 17,
            pixelX - 11,
            iconY + 14,
            "L",
            pixelX - 11,
            iconY + 5,
            "Q",
            pixelX - 11,
            iconY + 2,
            pixelX - 8,
            iconY + 2,
            "Z",
          ] as any)
          .attr({
            fill: isDark ? "#475569" : "#64748b",
            cursor: "pointer",
          })
          .add(group);

        // Comment count or icon inside
        chart.renderer
          .text(count > 1 ? String(count) : "✉", pixelX, iconY + 13)
          .attr({ align: "center" })
          .css({
            fontSize: count > 1 ? "10px" : "11px",
            fontWeight: "bold",
            color: "#fff",
            cursor: "pointer",
          })
          .add(group);

        // Tooltip on hover - show comments in chat-like format
        const shiftLabel = shiftName ? `Shift ${shiftName}` : "";
        const tooltipLines = cmts.map((c) => {
          const authorName = (c.author || "Unknown").split("@")[0];
          return `${authorName}: ${c.text}`;
        });
        const tooltipText =
          (shiftLabel ? `${shiftLabel}\\n` : "") + tooltipLines.join("\\n");
        group.on("mouseover", function () {
          const maxWidth = 280;
          const padding = 10;

          // Moving between adjacent markers can fire the next `mouseover`
          // before the previous `mouseout`, so clear any open callout first.
          destroyCommentTooltip();

          commentTooltipRef.current = chart.renderer
            .label(
              tooltipText,
              pixelX + 15,
              iconY,
              "callout",
              undefined,
              undefined,
              false,
              false,
              "comment-tooltip",
            )
            .attr({
              fill: isDark ? "#1e293b" : "#ffffff",
              stroke: isDark ? "#475569" : "#cbd5e1",
              "stroke-width": 1,
              r: 6,
              padding: padding,
              zIndex: 20,
            })
            .css({
              fontSize: "12px",
              color: isDark ? "#e2e8f0" : "#334155",
              width: `${maxWidth}px`,
              whiteSpace: "normal",
            })
            .add();
        });

        group.on("mouseout", destroyCommentTooltip);

        group.on("click", () => {
          destroyCommentTooltip();

          const first = cmts[0];
          const dateStr = new Date(tsKey).toISOString().split("T")[0];
          openPopover({
            x: pixelX,
            y: iconY + 30,
            category: dateStr,
            seriesName: shiftName ? `Shift ${shiftName}` : "",
            shiftDate: first.shift_date?.split("T")[0],
            shiftName: first.shift_name || shiftName,
            timestamp: first.timestamp,
          });
        });

        commentMarkersRef.current.push(group);
      });
    };

    // Comments changed → force the next draw past the signature check.
    lastMarkerKeyRef.current = "";
    drawMarkers();

    // `render` alone covers the initial render and every subsequent redraw, so
    // listening for `redraw` as well only doubled the work.
    Highcharts.addEvent(chart, "render", drawMarkers);

    const onLoad = () => setChartLoaded(true);
    Highcharts.addEvent(chart, "load", onLoad);

    return () => {
      destroyCommentTooltip();
      commentMarkersRef.current.forEach((m) => {
        try {
          m.destroy();
        } catch {}
      });
      commentMarkersRef.current = [];
      lastMarkerKeyRef.current = "";
      Highcharts.removeEvent(chart, "render", drawMarkers);
      Highcharts.removeEvent(chart, "load", onLoad);
    };
  }, [chartComments, isDark, commentsEnabled, openPopover, chartLoaded]);

  // Chart options for Highstock
  const chartOptions: Highcharts.Options = useMemo(() => {
    // Get themed base configuration for stock charts
    const themedConfig = getChartConfig({
      chart: {
        type: "stock" as any,
        height: height,
        // Enable pan and zoom functionality (configurable)
        panKey: "shift",
        panning: {
          enabled: panZoom.enabled,
          type: panZoom.type || "x",
        },
        zooming: {
          type: panZoom.type || "x",
          mouseWheel: {
            enabled: panZoom.mouseWheel !== false,
            sensitivity: 1.1,
          },
          pinchType:
            panZoom.pinchZoom !== false ? panZoom.type || "x" : undefined,
          resetButton: {
            theme: {
              display: "block",
              fill: statusColors.info,
              stroke: statusColors.info,
              r: 4,
              style: {
                color: "white",
                fontWeight: "bold",
                fontSize: "11px",
              },
              states: {
                hover: {
                  fill: statusColors.info,
                  stroke: statusColors.info,
                },
              },
            },
            position: {
              align: "right",
              verticalAlign: "top",
              x: -10,
              y: 10,
            },
          },
        },
        events: {
          selection: function (event: any): boolean {
            if (event.xAxis && onRangeChange) {
              const min = event.xAxis[0].min;
              const max = event.xAxis[0].max;
              onRangeChange(min, max);
            }
            return true;
          },
          load: function () {
            // Chart loaded, initially not zoomed
            setIsZoomed(false);
          },
        },
      },
      title: {
        text: undefined, // Remove chart title
      },
      subtitle: {
        text: undefined, // Remove chart subtitle
      },
      xAxis: {
        type: "datetime",
        labels: {
          style: {
            color: statusColors.yAxisLabels,
            fontSize: "12px",
          },
        },
        lineColor: statusColors.yAxisLabels,
        tickColor: statusColors.yAxisLabels,
        gridLineColor: isDark
          ? "rgba(71, 85, 105, 0.3)"
          : "rgba(226, 232, 240, 0.8)",
        crosshair: {
          width: 1,
          color: statusColors.plotLine,
          dashStyle: "Dash",
        },
        events: {
          afterSetExtremes: function (e: any) {
            // Skip if we're currently resetting
            if (isResettingRef.current) {
              return;
            }

            // Check zoom state when axis extremes change
            setTimeout(() => {
              // Only consider it zoomed if the event has min/max (user interaction)
              // and it's not the initial load or navigator selection that shows full range
              const hasUserZoom =
                e &&
                e.min !== undefined &&
                e.max !== undefined &&
                e.trigger !== "navigator" &&
                e.trigger !== "updatedData";
              setIsZoomed(hasUserZoom);
            }, 50);

            // Emit sync event for zoom synchronization (but avoid infinite loops)
            if (
              syncGroup &&
              e &&
              e.min !== undefined &&
              e.max !== undefined &&
              e.trigger !== "sync"
            ) {
              const extremesEvent = new CustomEvent(
                `extremes.sync.${syncGroup}`,
                {
                  detail: { min: e.min, max: e.max, trigger: "sync" },
                },
              );
              window.dispatchEvent(extremesEvent);
            }
          },
        },
      },
      yAxis: {
        min: yAxis.min,
        max: yAxis.max,
        softMin: yAxis.softMin,
        softMax: yAxis.softMax,
        startOnTick: yAxis.startOnTick,
        endOnTick: yAxis.endOnTick,
        title: {
          text: yAxis.title,
          style: {
            color: statusColors.yAxisTitle,
            fontSize: "13px",
            fontWeight: "600",
          },
        },
        opposite: false, // Position Y-axis on the left side
        labels: {
          format: yAxis.format ?? "{value}",
          align: "right",
          x: -5,
          style: {
            color: statusColors.yAxisLabels,
            fontSize: "12px",
          },
        },
        gridLineColor: isDark
          ? "rgba(71, 85, 105, 0.3)"
          : "rgba(226, 232, 240, 0.8)",
        lineColor: statusColors.yAxisLabels,
        tickColor: statusColors.yAxisLabels,
        plotLines: yAxis.plotLines?.map((plotLine) => ({
          value: plotLine.value,
          color: plotLine.color ?? statusColors.plotLine,
          dashStyle: plotLine.dashStyle ?? "Dash",
          width: plotLine.width ?? 2,
          label: plotLine.label
            ? {
                text: plotLine.label,
                align: "right",
                style: {
                  color: plotLine.color ?? statusColors.plotLine,
                  fontFamily: "IBM Plex Mono, monospace",
                },
              }
            : undefined,
        })),
      },
      legend: {
        enabled: legend.enabled && series.length > 1,
        align:
          legend.position === "left" || legend.position === "right"
            ? legend.position
            : "center",
        verticalAlign:
          legend.position === "top" || legend.position === "bottom"
            ? legend.position
            : "bottom",
        layout:
          legend.position === "left" || legend.position === "right"
            ? "vertical"
            : "horizontal",
      },
      plotOptions: {
        series: {
          marker: {
            enabled: type === "line" || type === "spline",
            radius: 3,
          },
          lineWidth: 2,
          // The pointer only turns into a hand when a left-click actually does
          // something. Comments live on RIGHT-click (see the contextmenu
          // effect) and must not change the cursor on every bar.
          cursor: isClickable ? "pointer" : undefined,
          point: {
            events: {
              click: function (this: any) {
                onPointClickRef.current?.({
                  x: this.x,
                  y: this.y ?? null,
                  actual: this.options?.custom?.actual ?? this.y ?? null,
                  seriesName: this.series.name,
                });
              },
            },
          },
        },
        column: {
          borderRadius: isDark ? 3 : 2,
          pointWidth: Math.min(
            25,
            Math.max(
              15,
              300 / Math.max(series.length > 0 ? series[0].data.length : 1, 1),
            ),
          ),
          grouping: true,
          // Column charts here plot pre-aggregated points (per shift/day), so
          // Highstock data grouping adds nothing and triggers a crash in
          // destroyGroupedData → pointAttribs on re-render while hovered.
          // (This is column-vs-column `grouping`, not time `dataGrouping`.)
          dataGrouping: { enabled: false },
          groupPadding: 0.1,
          pointPadding: 0.05,
          borderWidth: isDark ? 0 : 1,
          borderColor: isDark ? "transparent" : statusColors.yAxisLabels,
        },
        bar: {
          grouping: true, // Also configure for bar type
          groupPadding: 0.8,
          pointPadding: 0.05,
          borderWidth: isDark ? 0 : 1,
          borderColor: isDark ? "transparent" : statusColors.yAxisLabels,
        },
        area: {
          fillOpacity: isDark ? 0.4 : 0.3,
        },
        areaspline: {
          fillOpacity: isDark ? 0.4 : 0.3,
        },
      },
      series: series.map((s, index) => {
        const isStacked =
          customOptions.plotOptions?.column?.stacking === "normal" ||
          customOptions.plotOptions?.bar?.stacking === "normal";

        return {
          name: s.name,
          data: s.data,
          type: s.type || type,
          color: s.color ?? getSeriesColor(index),
          yAxis: s.yAxis,
          dashStyle: s.dashStyle,
          step: s.step,
          showInNavigator: isStacked ? index === 0 : true, // For stacked: only show first series in navigator, for non-stacked: show all
          navigatorOptions: isStacked
            ? {
                // For stacked charts, aggregate all series data for navigator
                type: type,
                stacking: "normal",
              }
            : undefined,
        };
      }) as any,
      tooltip: {
        enabled: true,
        shared: true,
        useHTML: true,
        backgroundColor: isDark ? "#1e293b" : "#ffffff",
        borderColor: isDark ? "#475569" : "#e2e8f0",
        borderRadius: 8,
        shadow: true,
        style: {
          color: isDark ? "#e2e8f0" : "#1e293b",
          fontSize: "12px",
        },
        formatter:
          tooltip.formatter ??
          function () {
            // @ts-ignore - Highcharts tooltip context
            const prefix = tooltip.prefix ?? "";
            const suffix = tooltip.suffix ?? "";
            const date = new Date(this.x);

            // Build tooltip content for all series at this timestamp
            let tooltipContent = `<b>${date.toLocaleDateString()}</b><br/>`;

            // Add each series value - check if this.points exists (shared tooltip)
            if (this.points && this.points.length > 0) {
              this.points.forEach((point: any) => {
                const seriesColor = point.series.color;
                const seriesName = point.series.name;
                // Prefer the unclamped value when the plotted `y` was capped to
                // keep the axis scale readable.
                const value = point.point?.options?.custom?.actual ?? point.y;

                tooltipContent += `<span style="color:${seriesColor}">●</span> ${seriesName}: <b>${prefix}${value}${suffix}</b><br/>`;
              });
            } else {
              // Fallback for single series tooltip
              const seriesName = this.series.name;
              const value = this.y;
              tooltipContent += `<span style="color:${this.series.color}">●</span> ${seriesName}: <b>${prefix}${value}${suffix}</b><br/>`;
            }

            return tooltipContent;
          },
      },
      navigator: {
        enabled: themedNavigator.enabled,
        height: themedNavigator.height || 80,
        maskFill: isDark
          ? "rgba(59, 130, 246, 0.15)"
          : "rgba(37, 99, 235, 0.15)",
        outlineColor: statusColors.info,
        outlineWidth: themedNavigator.outlineWidth || 2,
        handles: {
          backgroundColor: statusColors.info,
          borderColor: statusColors.info,
          width: 8,
          height: 20,
        },
        // Configure navigator to work with multiple series
        adaptToUpdatedData: true,
        series: {
          type: type, // Match the main chart type (column, line, etc.)
          includeInDataExport: false,
          lineWidth: 1,
          fillOpacity: 0.3,
        },
        xAxis: {
          labels: {
            style: {
              color: statusColors.yAxisLabels,
              fontSize: "10px",
            },
          },
        },
      },
      rangeSelector: {
        enabled: false,
      },
      scrollbar: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
      exporting: {
        enabled: exporting.enabled !== false,
        filename: exporting.filename || "stock-chart",
        buttons: {
          contextButton: {
            menuItems: [
              "viewFullscreen",
              "separator",
              "downloadPNG",
              "downloadJPEG",
              "downloadPDF",
              "downloadSVG",
            ],
          },
        },
      },
      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 600,
            },
            chartOptions: {
              legend: {
                enabled: false,
              },
              navigator: {
                height: 60, // Smaller navigator on mobile
              },
            },
          },
        ],
      },
    });

    let finalOptions = deepMerge(themedConfig, customOptions);

    // Force disable animations on mobile
    if (isMobile) {
      finalOptions.chart.animation = false;
      finalOptions.plotOptions = {
        ...finalOptions.plotOptions,
        series: {
          ...finalOptions.plotOptions?.series,
          animation: false,
        },
        line: { ...finalOptions.plotOptions?.line, animation: false },
        column: { ...finalOptions.plotOptions?.column, animation: false },
        bar: { ...finalOptions.plotOptions?.bar, animation: false },
        area: { ...finalOptions.plotOptions?.area, animation: false },
        spline: { ...finalOptions.plotOptions?.spline, animation: false },
        areaspline: {
          ...finalOptions.plotOptions?.areaspline,
          animation: false,
        },
      };
      finalOptions.responsive = {
        ...finalOptions.responsive,
        rules: [
          ...(finalOptions.responsive?.rules || []),
          {
            condition: {
              maxWidth: 768,
            },
            chartOptions: {
              chart: { animation: false },
              plotOptions: {
                series: { animation: false },
                line: { animation: false },
                column: { animation: false },
                bar: { animation: false },
                area: { animation: false },
                spline: { animation: false },
                areaspline: { animation: false },
              },
            },
          },
        ],
      };
    }

    return finalOptions;
  }, [
    type,
    title,
    subtitle,
    height,
    series,
    yAxis,
    legend,
    tooltip,
    customOptions,
    panZoom,
    themedNavigator,
    rangeSelector,
    onRangeChange,
    syncGroup,
    getChartConfig,
    getSeriesColor,
    isDark,
    commentProps,
    openPopover,
    isClickable,
  ]);

  // Create a separate memo for the final options to handle mobile overrides
  const finalChartOptions = useMemo(() => {
    let options = { ...chartOptions };

    // Force disable all animations on mobile
    if (isMobile) {
      const noAnimationOptions: Highcharts.Options = {
        chart: {
          animation: false,
        },
        plotOptions: {
          series: {
            animation: false,
          },
          // Disable initial animation for all series types
          line: { animation: false },
          column: { animation: false },
          bar: { animation: false },
          area: { animation: false },
          spline: { animation: false },
          areaspline: { animation: false },
        },
        tooltip: {
          animation: false,
        },
        // Add a responsive rule to be extra sure
        responsive: {
          rules: [
            {
              condition: {
                maxWidth: 768,
              },
              chartOptions: {
                chart: { animation: false },
                plotOptions: {
                  series: { animation: false },
                },
                navigator: {
                  enabled: false,
                },
                legend: {
                  enabled: false,
                },
              },
            },
          ],
        },
      };
      // Deep merge to override any existing animation settings
      options = deepMerge(options, noAnimationOptions);
    }

    return options;
  }, [chartOptions, isMobile]);

  /**
   * Memoised chart element.
   *
   * `highcharts-react-official` calls `chart.update()` on every render of its
   * parent, so unrelated state here — opening or closing the comment popover,
   * toggling the data table — made the whole chart redraw. Returning the same
   * element reference lets React skip that subtree entirely unless the options
   * (or the remount `key`) actually changed.
   */
  const chartElement = useMemo(
    () => (
      <HighchartsReact
        key={`chart-${isDark ? "dark" : "light"}-${height}-${isMobile}`}
        highcharts={Highcharts}
        constructorType={"stockChart"}
        options={finalChartOptions}
        ref={chartRef}
        callback={(_chart: any) => {
          // Chart is now ready - trigger comment marker drawing
          setChartLoaded(true);
        }}
      />
    ),
    [finalChartOptions, isDark, height, isMobile],
  );

  // Error state
  if (error) {
    return (
      <div className={`flex items-center justify-center h-64 ${className}`}>
        <div className="text-center">
          <div className="text-red-500 font-medium mb-2">
            {t("charts.errorLoadingData")}
          </div>
          <div className="text-gray-500 text-sm mb-3">{error}</div>
          {onRetry && (
            <button
              onClick={onRetry}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
            >
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`relative ${className}`}
      style={{ height: height }}
    >
      {/* Loading overlay - only show when we have data and are refreshing */}
      {loading &&
        series.length > 0 &&
        series.some((s) => s.data.length > 0) && (
          <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10 rounded-lg">
            <div className="flex items-center space-x-2">
              <Spinner size={20} className="text-blue-600" />
              <span className="text-gray-600 font-medium">
                {t("charts.loadingData")}
              </span>
            </div>
          </div>
        )}
      {/* Chart or no data message */}
      {series.length > 0 && series.some((s) => s.data.length > 0) ? (
        <div className="relative" style={{ height: height }}>
          {/* Custom Reset Zoom Button - Only when zoomed */}
          {isZoomed && (
            <button
              onClick={resetZoom}
              className="absolute top-4 left-4 z-20 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded shadow-lg transition-all duration-200 border border-blue-700"
              title="Réinitialiser le zoom pour afficher toutes les données"
            >
              ↻ Reset Zoom
            </button>
          )}
          {chartElement}
          {/* Controls row: extra controls left, action buttons right */}
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {extraControls && <div className="mr-auto">{extraControls}</div>}
            <div
              className="flex items-center gap-1.5 ml-auto"
              data-tour="chart-export"
            >
              <button
                onClick={exportToCSV}
                className="px-2 sm:px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 transition-colors text-xs font-medium"
                title="Export raw data as CSV file"
              >
                CSV
              </button>
              <button
                onClick={exportToExcel}
                className="px-2 sm:px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 transition-colors text-xs font-medium"
                title="Export raw data as Excel file"
              >
                Excel
              </button>
            </div>
            <button
              onClick={() => setShowData((v) => !v)}
              className="px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors text-xs sm:text-sm font-medium"
            >
              {showData
                ? t("charts.hideData") || "Hide Data"
                : t("charts.showData") || "Show Data"}
            </button>
          </div>
          {/* Data Grid */}
          {showData && (
            <div className="overflow-x-auto mt-2">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead style={{ backgroundColor: "var(--bg-secondary)" }}>
                  <tr>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider"
                      style={{ color: "var(--text-secondary)" }}
                    >
                      Timestamp
                    </th>
                    {series.map((s, idx) => (
                      <th
                        key={idx}
                        className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider"
                        style={{ color: "var(--text-secondary)" }}
                      >
                        {s.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody
                  className="divide-y divide-gray-200 dark:divide-gray-700"
                  style={{ backgroundColor: "var(--bg-primary)" }}
                >
                  {/* Build a merged table by timestamp */}
                  {(() => {
                    // Collect all unique timestamps
                    const allTimestamps = Array.from(
                      new Set(series.flatMap((s) => s.data.map(pointX))),
                    ).sort((a, b) => a - b);
                    return allTimestamps.map((ts, rowIdx) => (
                      <tr key={rowIdx}>
                        <td
                          className="px-4 py-3 whitespace-nowrap text-sm"
                          style={{ color: "var(--text-primary)" }}
                        >
                          {new Date(ts).toLocaleString()}
                        </td>
                        {series.map((s, colIdx) => {
                          const found = s.data.find((d) => pointX(d) === ts);
                          const value = found ? pointY(found) : null;
                          return (
                            <td
                              key={colIdx}
                              className="px-4 py-3 whitespace-nowrap text-sm font-mono"
                              style={{ color: "var(--text-primary)" }}
                            >
                              {value !== null && value !== undefined
                                ? value
                                : "-"}
                            </td>
                          );
                        })}
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : loading ? (
        <div
          className="flex flex-col items-center justify-center"
          style={{ height }}
        >
          <div className="flex flex-col items-center space-y-4">
            <div className="relative">
              <div className="animate-spin h-10 w-10 border-4 border-blue-600 border-t-transparent rounded-full"></div>
            </div>
            <span className="text-gray-500 font-medium">
              {t("charts.loadingData")}
            </span>
          </div>
          {/* Skeleton line chart */}
          <div className="flex items-end justify-center space-x-1 mt-6 opacity-30">
            <div className="w-3 h-12 bg-gray-300 rounded animate-pulse"></div>
            <div
              className="w-3 h-20 bg-gray-300 rounded animate-pulse"
              style={{ animationDelay: "0.05s" }}
            ></div>
            <div
              className="w-3 h-16 bg-gray-300 rounded animate-pulse"
              style={{ animationDelay: "0.1s" }}
            ></div>
            <div
              className="w-3 h-24 bg-gray-300 rounded animate-pulse"
              style={{ animationDelay: "0.15s" }}
            ></div>
            <div
              className="w-3 h-18 bg-gray-300 rounded animate-pulse"
              style={{ animationDelay: "0.2s" }}
            ></div>
            <div
              className="w-3 h-28 bg-gray-300 rounded animate-pulse"
              style={{ animationDelay: "0.25s" }}
            ></div>
            <div
              className="w-3 h-22 bg-gray-300 rounded animate-pulse"
              style={{ animationDelay: "0.3s" }}
            ></div>
            <div
              className="w-3 h-14 bg-gray-300 rounded animate-pulse"
              style={{ animationDelay: "0.35s" }}
            ></div>
            <div
              className="w-3 h-26 bg-gray-300 rounded animate-pulse"
              style={{ animationDelay: "0.4s" }}
            ></div>
            <div
              className="w-3 h-20 bg-gray-300 rounded animate-pulse"
              style={{ animationDelay: "0.45s" }}
            ></div>
          </div>
        </div>
      ) : (
        <div
          className="flex items-center justify-center text-gray-500"
          style={{ height }}
        >
          <div className="text-center">
            <div className="text-lg font-medium mb-2">
              {t("charts.noDataAvailable")}
            </div>
            <div className="text-sm">{noDataMessage}</div>
          </div>
        </div>
      )}

      {/* Comment popover — appears on point click */}
      {popoverPoint && commentsEnabled && (
        <Suspense fallback={null}>
          <ChartCommentPopover
            chartType={commentProps.chartType}
            machineBK={commentProps.machineBK}
            point={popoverPoint}
            onClose={() => {
              closePopover();
              refetchComments();
            }}
            containerRef={containerRef}
          />
        </Suspense>
      )}
    </div>
  );
};

export default BaseStockChart;
